/**
 * Lift2Fit — block & goal presentation primitives.
 *
 * The summary and goals screens share these so the percentage bars, the week
 * rows and the compact Grid panel read identically. Colour is a status hint on
 * a bar, a chip or a small line — never on the headline number itself.
 */
import type { ReactNode } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';
import { CheckIcon, TrendDownIcon } from '@/lib/icons';
import { cn, formatDate } from '@/lib/format';
import { formatVolume, formatWeight } from '@/lib/units';
import type { BlockWeek, Goal, RecentSet } from '@/lib/api';
import { GOAL_METRIC_LABELS, GOAL_METRIC_UNITS } from './demoData';
import { StatusChip } from './components';

export interface GoalState {
  achieved: boolean;
  behind: boolean;
  /** small, non-numeric status word + glyph */
  label: string;
}

/**
 * Shown when a screen fell back to the built-in dataset for a REAL account —
 * the global demo banner is tied to the demo identity, so without this a live
 * user could be looking at demo numbers and not know it.
 */
export function SourceNotice({ source, isDemoSession }: { source: 'api' | 'demo' | null; isDemoSession: boolean }) {
  if (source !== 'demo' || isDemoSession) return null;
  return (
    <p
      data-testid="fallback-note"
      role="status"
      className="border border-hairline border-l-2 border-l-warning bg-page-subtle px-3 py-2 text-[12px] text-ink-muted"
    >
      This screen could not reach that part of the API, so it is showing the built-in demo set — not your account.
    </p>
  );
}

/** One definition of "achieved / behind / on track" for every surface. */
export function goalState(goal: Goal): GoalState {
  // `progressPct` from the API is already direction-aware: it encodes "closer
  // to the target" for an up-metric and for the lower-is-better pain metric
  // alike, and it stays 0 while a pain goal has no logged evidence. Using it as
  // the source of truth means a pain goal is never read as achieved just
  // because its average is low by default.
  const achieved = goal.status === 'achieved' || goal.progressPct >= 100;
  if (achieved) return { achieved, behind: false, label: 'Achieved' };
  if (goal.status === 'missed' || goal.progressPct < 60) return { achieved, behind: true, label: 'Behind' };
  return { achieved, behind: false, label: 'On track' };
}

export function GoalStatusChip({ goal }: { goal: Goal }) {
  const state = goalState(goal);
  if (state.achieved) {
    return (
      <StatusChip tone="success" icon={<CheckIcon />}>
        {state.label}
      </StatusChip>
    );
  }
  if (state.behind) {
    return (
      <StatusChip tone="warning" icon={<TrendDownIcon />}>
        {state.label}
      </StatusChip>
    );
  }
  return <StatusChip tone="neutral">{state.label}</StatusChip>;
}

function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function goalValue(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return rounded.toLocaleString('en-GB');
}

/** Adherence tone for a week row — the bar carries the colour, the number stays ink. */
function adherenceTone(pct: number): string {
  if (pct >= 80) return 'bg-success';
  if (pct >= 60) return 'bg-warning';
  return 'bg-danger';
}

/**
 * A goal as a percentage bar: title, the metric in plain English, current vs
 * target with its unit, the percentage, and a small status hint.
 */
export function GoalProgressBar({
  goal,
  actions,
  testId = 'goal-bar',
}: {
  goal: Goal;
  actions?: ReactNode;
  testId?: string;
}) {
  const state = goalState(goal);
  const pct = clampPct(goal.progressPct);
  const fill = state.achieved ? 'bg-success' : state.behind ? 'bg-warning' : 'bg-ink';
  // The live API can return an empty unit string; fall back to the metric's own.
  const unit = goal.unit || GOAL_METRIC_UNITS[goal.metric];
  // A pain goal with no logged pain reads 0 at 0% progress; a genuinely
  // pain-free block reads 0 at 100% (achieved), which is the distinction.
  const painAwaitingData = goal.metric === 'pain_average' && goal.current === 0 && goal.progressPct < 100;
  return (
    <li data-testid={testId} className="flex flex-col gap-2 border-b border-hairline py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[14px] font-bold leading-snug text-ink">{goal.title}</span>
          <span className="text-[12px] text-ink-muted">{GOAL_METRIC_LABELS[goal.metric]}</span>
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <GoalStatusChip goal={goal} />
          {actions}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-pill bg-page-subtle" role="presentation">
          <span className={cn('block h-full rounded-pill', fill)} style={{ width: `${pct}%` }} />
        </span>
        <span className="l2f-tabular shrink-0 font-mono text-[13px] font-bold text-ink">{pct}%</span>
      </div>

      <p className="text-[12px] text-ink-muted">
        {goal.metric === 'pain_average' ? (
          <>
            {/* Lower is better, so the row states the ceiling, not "current of target". */}
            Average pain ≤ <span className="l2f-tabular font-mono font-bold text-ink">{goalValue(goal.target)}</span>
            <span className="mx-1.5 text-hairline">|</span>
            {painAwaitingData ? (
              'no pain logged yet'
            ) : (
              <>
                now <span className="l2f-tabular font-mono font-bold text-ink">{goalValue(goal.current)}</span> / 10
              </>
            )}
          </>
        ) : (
          <>
            <span className="l2f-tabular font-mono font-bold text-ink">{goalValue(goal.current)}</span> of{' '}
            <span className="l2f-tabular font-mono font-bold text-ink">{goalValue(goal.target)}</span> {unit}
          </>
        )}
        <span className="mx-1.5 text-hairline">|</span>
        due {formatDate(goal.dueOn)}
      </p>
    </li>
  );
}

/** The current block's four ISO weeks, oldest first, current week marked. */
export function BlockWeekRows({ weeks, unit }: { weeks: BlockWeek[]; unit: UnitSystem }) {
  if (!weeks.length) return null;
  return (
    <ol className="flex flex-col divide-y divide-hairline" data-testid="block-weeks">
      {weeks.map((week, index) => (
        <li key={week.weekStart} data-testid="block-week-row" className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-bold uppercase tracking-[0.06em] text-ink">Week {index + 1}</span>
              <span className="text-[12px] text-ink-muted">from {formatDate(week.weekStart)}</span>
              {week.isCurrent ? <StatusChip tone="accent">Current week</StatusChip> : null}
            </span>
            <span className="l2f-tabular font-mono text-[13px] font-bold text-ink">
              {week.sessions} / {week.target} sessions
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-pill bg-page-subtle" role="presentation">
              <span
                className={cn('block h-full rounded-pill', adherenceTone(week.adherencePct))}
                style={{ width: `${clampPct(week.adherencePct)}%` }}
              />
            </span>
            <span className="l2f-tabular shrink-0 font-mono text-[12px] font-bold text-ink">{week.adherencePct}%</span>
          </div>
          <span className="text-[12px] text-ink-muted">
            <span className="l2f-tabular font-mono font-bold text-ink">{week.sets}</span> sets ·{' '}
            {formatVolume(week.volumeKg, unit)}
          </span>
        </li>
      ))}
    </ol>
  );
}

/**
 * A compact view of the most recent logged sets. The full interactive Grid
 * lives at the in-app `/grid`; this is deliberately a read-only summary table.
 */
export function RecentSetsTable({ sets, unit }: { sets: RecentSet[]; unit: UnitSystem }) {
  return (
    <div className="l2f-grid-scroll">
      <table className="w-full min-w-[400px] border-separate border-spacing-0 text-left">
        <caption className="l2f-visually-hidden">
          The most recent sets you logged in this block: movement, weight, reps and RPE.
        </caption>
        <thead>
          <tr>
            <th scope="col" className="l2f-grid-corner w-[45%] border-b border-hairline px-3 py-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Movement</span>
            </th>
            {['Weight', 'Reps', 'RPE'].map((label) => (
              <th key={label} scope="col" className="l2f-grid-head border-b border-l border-hairline px-3 py-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sets.map((set) => (
            <tr key={set.id}>
              <th scope="row" className="l2f-grid-col border-b border-hairline px-3 py-2 align-top text-left">
                <span className="flex flex-col">
                  <span className="text-[13px] font-bold leading-tight text-ink">{set.movement}</span>
                  <span className="text-[11px] text-ink-muted">
                    {set.variant ? `${set.variant} · ` : ''}
                    {set.sessionLabel}
                  </span>
                </span>
              </th>
              <td className="l2f-tabular border-b border-l border-hairline px-3 py-2 font-mono text-[13px] text-ink">
                {set.weightKg === null ? '—' : formatWeight(set.weightKg, unit)}
              </td>
              <td className="l2f-tabular border-b border-l border-hairline px-3 py-2 font-mono text-[13px] text-ink">
                {set.reps ?? '—'}
              </td>
              <td className="l2f-tabular border-b border-l border-hairline px-3 py-2 font-mono text-[13px] text-ink">
                {set.rpe ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
